import Box from "@mui/material/Box";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import mapboxgl from "mapbox-gl";
import { useTheme } from "@mui/material/styles";

import useMediaQuery from "@mui/material/useMediaQuery";
import "mapbox-gl/dist/mapbox-gl.css";
import "paper-css/paper.css";
import mapStyle from "./mapStyle";
import {
  initialState,
  actionMapHandleScreenSize,
  getPadding,
} from "./MapRedux";
import {
  mapClickHandler,
  mapMoveendHandler,
  mapClickBuildingHandler,
  mapClickRoomHandler,
  mapDbclickBuildingHandler,
  mapDbclickRoomHandler,
  mapMouseoverBuildingHandler,
  mapMouseoverRoomHandler,
  mapMouseoutBuildingHandler,
  mapMouseoutRoomHandler,
  mapErrorHandler,
  mapDataSourceLoadedHandler,
  mapTouchHandler,
} from "./mapEventHandlers";
import {
  mapDrawCreateHandler,
  mapDrawUpdateHandler,
  mapDrawModeChangeHandler,
} from "./mapDrawEventHandlers";
import {
  handleMapViewChange,
  handleSourceLayersAdd,
  handleLayerChange,
  handleSelectFeatures,
  handleFloorChange,
  handlePrint,
  handleZoomFeatures,
  handleCursorChange,
  handleDrawModeChange,
  handleDrawFeaturesChange,
  handleActiveFeatureChange,
  handleSingleBuildingMode,
  handleIconAndLabelOffset,
} from "./mapEffectHandlers";
import { handleCustomLayers } from "./mapSheetEffectHandlers";
import { addIconsToMap } from "../Spreadsheet/IconSelector";
import {
  actionAddDefaultLayers,
  selectorLayerChangeRequest,
  selectorLayerIntialSourceLayers,
  selectorLayerSingleBuildingMode,
} from "../Layer";
import { selectorDataSelectFeatureRequest } from "../Data";
import { selectorAppBarFloor } from "../AppBar";
import { selectorAuthIdToken, urlShowsLoginPending } from "../Auth";
import { selectorSheets } from "../Spreadsheet";
import { actionUpdateMapviewHistory } from "../App";
import { scaleGridEventHandler } from "../ScaleGrid";

import MapboxWorker from "mapbox-gl/dist/mapbox-gl-csp-worker?worker";
mapboxgl.workerClass = MapboxWorker;

export default function Map() {
  const dispatch = useDispatch();
  const mapContainer = useRef(null);

  const [map, setMap] = useState(null);

  const theme = useTheme();
  const xsDown = useMediaQuery(theme.breakpoints.down("sm"));
  const smDown = useMediaQuery(theme.breakpoints.down("md"));
  const screenSize = { xsDown, smDown };


  const mapPadding = useSelector((state) => state.mapReducer.padding);
  const mapviewChangeRequest = useSelector(
    (state) => state.mapReducer.mapviewChangeRequest,
  );
  const print = useSelector((state) => state.mapReducer.print);
  const cursor = useSelector((state) => state.mapReducer.cursor);
  const zoomFeatures = useSelector((state) => state.mapReducer.zoomFeatures);
  const drawMode = useSelector((state) => state.mapReducer.drawMode);
  const drawFeatures = useSelector((state) => state.mapReducer.drawFeatures);
  const activeFeature = useSelector((state) => state.mapReducer.activeFeature);

  // external components
  const idToken = useSelector(selectorAuthIdToken);
  const initialSourceLayers = useSelector(selectorLayerIntialSourceLayers);
  const layerChangeRequest = useSelector(selectorLayerChangeRequest);
  const singleBuildingMode = useSelector(selectorLayerSingleBuildingMode);
  const selectedFloor = useSelector(selectorAppBarFloor);
  const selectFeatureRequest = useSelector(selectorDataSelectFeatureRequest);
  const customSheets = useSelector(selectorSheets);
  const label = useSelector((state) => state.layerReducer.label);
  const color = useSelector((state) => state.layerReducer.color);
  const sawFirstRenderedFloor = useRef(false);

  // initialize
  useEffect(() => {
    if (urlShowsLoginPending()) {
      // If we are finishing up an OIDC auth flow, don't bother initializing
      // the map since, in a split second, we'll have the idToken and we'll
      // be doing it again anyway. This avoids some visual noise during
      // login where the map would draw without buildings (since no auth)
      // and then redraw with buildings soon after.
      return;
    }

    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;
    const { lng, lat, zoom } = initialState;
    const padding = getPadding(screenSize);
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [lng, lat],
      zoom: zoom,
      preserveDrawingBuffer: true,
      bearingSnap: 0,
      // authorization header added when we request mgis tiles
      transformRequest: (url, resourceType) => {
        if (
          resourceType === "Tile" &&
          url.startsWith("https://" + import.meta.env.VITE_DOMAIN)
        ) {
          const headers = { Authorization: "Bearer " + idToken };
          return { url, headers };
        }
      },
    });

    map.setPadding(padding);
    map.on("load", () => {
      window.map = map; // debug
      setMap(map);

      map.resize();
      map.on("click", mapClickHandler(map, dispatch));
      map.on("click", "umich-building", mapClickBuildingHandler(map, dispatch));
      map.on("click", "umich-room", mapClickRoomHandler(map, dispatch));
      map.on("touchstart", mapTouchHandler(map, dispatch));
      map.on(
        "dblclick",
        "umich-building",
        mapDbclickBuildingHandler(map, dispatch),
      );
      map.on("dblclick", "umich-room", mapDbclickRoomHandler(map, dispatch));
      map.on(
        "mouseover",
        "umich-building",
        mapMouseoverBuildingHandler(map, dispatch),
      );
      map.on("mouseover", "umich-room", mapMouseoverRoomHandler(map, dispatch));
      map.on(
        "mouseout",
        "umich-building",
        mapMouseoutBuildingHandler(map, dispatch),
      );
      map.on("mouseout", "umich-room", mapMouseoutRoomHandler(map, dispatch));
      map.on(
        "moveend",
        mapMoveendHandler(map, dispatch, sawFirstRenderedFloor),
      );
      map.on("error", mapErrorHandler(map, dispatch));
      map.on("sourcedata", mapDataSourceLoadedHandler(dispatch));

      // draw events
      map.on("draw.create", mapDrawCreateHandler(map, dispatch));
      map.on("draw.update", mapDrawUpdateHandler(map, dispatch));
      //   map.on('draw.delete', function (e) { console.log('draw.delete', e.features); });
      //   map.on('draw.selectionchange', function (e) { console.log('draw.selectionchange', e.features); });
      map.on("draw.modechange", mapDrawModeChangeHandler(map, dispatch));
      map.on("moveend", scaleGridEventHandler(map, dispatch));

      dispatch(actionMapHandleScreenSize(screenSize));
      if (idToken) {
        // Avoid asking for tiles if we are not authorized yet.
        dispatch(actionAddDefaultLayers(import.meta.env.VITE_DOMAIN));
      }
      dispatch(
        actionUpdateMapviewHistory({ lng, lat, zoom, pitch: 0, bearing: 0 }),
      );

      // add Material Icons
      addIconsToMap(map);
    });
    return () => {
      map.remove();
    };
  }, [idToken]);

  // change mapview
  useEffect(() => {
    handleMapViewChange(map, mapviewChangeRequest, mapPadding);
  }, [map, mapPadding, mapviewChangeRequest]);

  // add source and layers
  useEffect(() => {
    handleSourceLayersAdd(map, initialSourceLayers);
  }, [map, initialSourceLayers]);

  // change layers
  useEffect(() => {
    handleLayerChange(map, layerChangeRequest, color);
  }, [map, layerChangeRequest, color]);

  // select/mark rooms @todo: this attr should belong to maps
  useEffect(() => {
    handleSelectFeatures(map, selectFeatureRequest);
  }, [map, selectFeatureRequest]);

  // floor change
  useEffect(() => {
    handleFloorChange(map, selectedFloor, dispatch, singleBuildingMode);
  }, [map, selectedFloor]);

  // print
  useEffect(() => {
    handlePrint(map, mapContainer, dispatch, print, screenSize);
  }, [map, mapContainer, dispatch, print]);

  // spreadsheet
  useEffect(() => {
    handleCustomLayers(
      map,
      customSheets,
      selectedFloor,
      idToken,
      dispatch,
      singleBuildingMode,
    );
  }, [map, customSheets, idToken, singleBuildingMode]);

  useEffect(handleZoomFeatures(map, zoomFeatures, mapPadding, idToken), [
    map,
    zoomFeatures,
    mapPadding,
    idToken,
  ]);
  useEffect(handleCursorChange(map, cursor), [map, cursor]); // cursor change
  useEffect(handleDrawModeChange(map, dispatch, drawMode), [map, drawMode]); // draw
  useEffect(handleDrawFeaturesChange(map, drawFeatures), [map, drawFeatures]);
  useEffect(handleActiveFeatureChange(map, activeFeature), [
    map,
    activeFeature,
  ]);
  useEffect(handleSingleBuildingMode(map, singleBuildingMode), [
    map,
    singleBuildingMode,
  ]);
  useEffect(handleIconAndLabelOffset(map, label), [map, label]);

  return <Box id="map" ref={mapContainer} sx={mapStyle} />;
}
